import { useCallback } from "react";
import { Item } from "@thenounproject/lingo-core";

import { IconStatus } from "../types";
import { useAssetCopy } from "./useAssetCopy";

export type CopyStatus = {
  fileType: string;
  status: IconStatus;
};

/**
 * Convenience hook for copying a specific item's asset to clipboard.
 * Delegates to useAssetCopy for the actual clipboard logic.
 *
 * Use useAssetCopy directly when you need to copy arbitrary assets
 * (e.g., in the action menu system).
 */
export function useFileCopy(item: Item) {
  const { copySingle, copyState, isCopying } = useAssetCopy();

  const copyAsset = useCallback(
    async (fileType: string, dimensions: string, dpi?: number, e?: React.MouseEvent) => {
      if (e) e.preventDefault();
      const asset = item.asset;
      if (!asset) return;
      await copySingle({ item, asset }, { fileType, dimensions, dpi });
    },
    [item, copySingle]
  );

  const copyStatus: CopyStatus = {
    fileType: copyState.fileType || "",
    status: copyState.status,
  };

  return {
    copyAsset,
    copyStatus,
    isCopying,
  };
}
